<script>
import CommonSubtitle from "../../CommonSubtitle/index.vue";
import CustomTextField from "../../CustomInput/CustomTextField.vue";
import { mapState } from "vuex";
import CustomSelect from "../../global/CustomSelect/index.vue";
import { Genders } from "../../../utils/constants";

export default {
  name: "DanInfo",
  components: { CustomSelect, CustomTextField, CommonSubtitle },
  props: {
    danInfo: {
      type: Object,
      required: true,
    },
    submitted: {
      type: Boolean,
      required: false,
      default: false,
    },
    v$: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      genders: Genders.map((item) => ({ id: item.id, label: item.name })),
      sex: null,
    };
  },
  computed: {
    ...mapState("user", ["danState", "tempUser", "user"]),
    isChildUser() {
      return ["child", "children"].includes(this.user?.individual);
    },
  },
  methods: {
    isBlank(value) {
      return value === null || value === undefined || `${value}`.trim() === "";
    },
    getSexFromRegisterNumber(registerNumber) {
      if (this.isBlank(registerNumber)) {
        return null;
      }

      const registerDigits = `${registerNumber}`.replace(/\D/g, "");
      if (registerDigits.length < 2) {
        return null;
      }

      const genderDigit = Number(registerDigits.charAt(registerDigits.length - 2));
      if (Number.isNaN(genderDigit)) {
        return null;
      }

      return genderDigit % 2 === 0 ? 1 : 0;
    },
    resolveSexValue(sex, registerNumber) {
      if (!this.isBlank(sex)) {
        return typeof sex === "string" && /^\d+$/.test(sex) ? Number(sex) : sex;
      }

      return this.getSexFromRegisterNumber(registerNumber);
    },
    applyDanInfo(source, danVerified) {
      if (!source) {
        return;
      }

      this.danInfo.birthday = source.birthday;
      this.danInfo.middlename = source.middlename;
      this.danInfo.lastname = source.lastname;
      this.danInfo.firstname = source.firstname;
      this.danInfo.civil_id = source.civil_id;
      this.danInfo.register_number = source.register_number;
      this.danInfo.sex = this.resolveSexValue(source.sex, source.register_number);
      this.danInfo.dan_verified = danVerified;
      this.danInfo.passport_expire_date = source.passport_expire_date;
    },
    hasError(field) {
      const validationError = !!this.v$?.[field]?.$error;
      if (!this.isChildUser) {
        return validationError;
      }

      const requiredFields = ["firstname", "lastname", "register_number", "birthday", "sex"];
      if (!requiredFields.includes(field)) {
        return validationError;
      }

      return validationError || (this.submitted && this.isBlank(this.danInfo[field]));
    },
  },
  watch: {
    "danInfo.register_number"(registerNumber) {
      if (!this.isBlank(this.danInfo.sex)) {
        return;
      }

      const resolvedSex = this.getSexFromRegisterNumber(registerNumber);
      if (resolvedSex !== null) {
        this.danInfo.sex = resolvedSex;
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.user) {
        this.applyDanInfo(
          this.user,
          this.isChildUser ? Number(this.user.dan_verified || 0) : 1,
        );
      }
      if (this.danState === true) {
        this.$bvToast.toast("Дангаар баталгаажуулалт амжилттай", {
          title: "Амжилттай",
          variant: "success",
        });
        this.applyDanInfo(
          this.tempUser,
          this.isChildUser ? Number(this.user?.dan_verified || 0) : 1,
        );
      }
    });
  },
};
</script>

<template>
  <div>
    <CommonSubtitle :name="$t('kyc.profile')" />
    <div class="ml-4 mr-4">
      <div class="row">
        <div class="col-sm-12 col-md-6 col-lg-4 col-xl-4">
          <CustomTextField
            :label="$t('kyc.surname')"
            v-model="danInfo.middlename"
            :disabled="!isChildUser"
          />
        </div>
        <div class="col-sm-12 col-md-6 col-lg-4 col-xl-4">
          <CustomTextField
            :label="$t('kyc.lastname')"
            v-model="danInfo.lastname"
            :disabled="!isChildUser"
            :error="hasError('lastname')"
            :error-message="$t('kyc.fill_this')"
          />
        </div>
        <div class="col-sm-12 col-md-6 col-lg-4 col-xl-4">
          <CustomTextField
            :label="$t('kyc.firstname')"
            v-model="danInfo.firstname"
            :disabled="!isChildUser"
            :error="hasError('firstname')"
            :error-message="$t('kyc.fill_this')"
          />
        </div>
        <div class="col-sm-12 col-md-6 col-lg-4 col-xl-4">
          <CustomTextField
            :label="$t('kyc.register_number')"
            v-model="danInfo.register_number"
            :disabled="!isChildUser"
            :error="hasError('register_number')"
            :error-message="$t('kyc.fill_this')"
          />
        </div>
        <div class="col-sm-12 col-md-6 col-lg-4 col-xl-4">
          <CustomTextField
            :label="$t('kyc.civil_id')"
            v-model="danInfo.civil_id"
            :disabled="!isChildUser"
          />
        </div>
        <div class="col-sm-12 col-md-6 col-lg-4 col-xl-4">
          <CustomTextField
            :label="$t('kyc.birth_date')"
            v-model="danInfo.birthday"
            type="date"
            :disabled="!isChildUser"
            :error="hasError('birthday')"
            :error-message="$t('kyc.fill_this')"
          />
        </div>
        <div class="col-sm-12 col-md-6 col-lg-4 col-xl-4">
          <CustomSelect
            :label="$t('kyc.gender')"
            v-model="danInfo.sex"
            :options="genders"
            :disabled="!isChildUser"
            :error="hasError('sex')"
            :error-message="$t('kyc.fill_this')"
          />
        </div>
        <div class="col-sm-12 col-md-6 col-lg-4 col-xl-4">
          <CustomTextField
            :label="$t('kyc.passport_exp_date')"
            v-model="danInfo.passport_expire_date"
            type="date"
            :disabled="!isChildUser"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
